import { useState, useEffect, useCallback, Component } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useGeolocation } from '@/hooks/useGeolocation';
import { formatDistance, getAccuracyInfo } from '@/lib/geoUtils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  Pin,
  useMap
} from '@vis.gl/react-google-maps';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
const hasValidMapKey = typeof GOOGLE_MAPS_API_KEY === 'string' && GOOGLE_MAPS_API_KEY.trim().length > 10;

// Simple error boundary for the map component
class MapErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('Map error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500 text-sm">
          Map unavailable
        </div>
      );
    }
    return this.props.children;
  }
}

// Geofence circle overlay component
function GeofenceCircle({ center, radius, isInside }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !center || !radius) return;

    // Check if Google Maps library is available
    if (!window.google?.maps?.Circle) {
      console.warn('Google Maps Circle not available');
      return;
    }

    try {
      const circle = new window.google.maps.Circle({
        map,
        center,
        radius,
        fillColor: isInside ? '#22c55e' : '#ef4444',
        fillOpacity: 0.15,
        strokeColor: isInside ? '#22c55e' : '#ef4444',
        strokeOpacity: 0.8,
        strokeWeight: 2
      });

      return () => circle.setMap(null);
    } catch (err) {
      console.error('Error creating geofence circle:', err);
    }
  }, [map, center, radius, isInside]);

  return null;
}

// Map content with markers and geofence
function CheckInMapContent({ siteCenter, userPosition, radius, isInside }) {
  const map = useMap();

  // Fit bounds to show both markers
  useEffect(() => {
    if (!map || !siteCenter) return;

    // Check if Google Maps library is available
    if (!window.google?.maps?.LatLngBounds) {
      console.warn('Google Maps not fully loaded');
      return;
    }

    try {
      if (userPosition) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(siteCenter);
        bounds.extend({ lat: userPosition.latitude, lng: userPosition.longitude });
        // Add some padding
        map.fitBounds(bounds, { top: 40, bottom: 40, left: 40, right: 40 });
      } else {
        // Just show the geofence area
        const circle = new window.google.maps.Circle({ center: siteCenter, radius });
        const bounds = circle.getBounds();
        if (bounds) {
          map.fitBounds(bounds, { top: 30, bottom: 30, left: 30, right: 30 });
        }
      }
    } catch (err) {
      console.error('Error fitting map bounds:', err);
    }
  }, [map, siteCenter, userPosition, radius]);

  return (
    <>
      {/* Geofence circle */}
      <GeofenceCircle center={siteCenter} radius={radius} isInside={isInside} />

      {/* Site center marker */}
      {siteCenter && (
        <AdvancedMarker position={siteCenter} title="Site Location">
          <Pin background="#3b82f6" borderColor="#fff" glyphColor="#fff" />
        </AdvancedMarker>
      )}

      {/* User position marker */}
      {userPosition && (
        <AdvancedMarker
          position={{ lat: userPosition.latitude, lng: userPosition.longitude }}
          title="Your Location"
        >
          <div className="relative">
            <div className={`w-4 h-4 rounded-full border-2 border-white shadow-lg ${
              isInside ? 'bg-green-500' : 'bg-red-500'
            }`}>
              <div className={`absolute inset-0 rounded-full animate-ping opacity-75 ${
                isInside ? 'bg-green-400' : 'bg-red-400'
              }`} />
            </div>
          </div>
        </AdvancedMarker>
      )}
    </>
  );
}

import {
  MapPin,
  LogIn,
  LogOut,
  Clock,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Navigation,
  ShieldAlert,
  ShieldCheck,
  Target,
  RefreshCw,
  Nfc,
  Map as MapIcon
} from 'lucide-react';
import { LoadingSpinner } from '@/components/LoadingSpinner';

export const MobileCheckInPage = () => {
  const { qrCode } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();

  // Detect if this is an NFC-triggered check-in
  const source = searchParams.get('source');
  const isNFCSource = source === 'nfc';

  usePageTitle(isNFCSource ? 'NFC Check In' : 'Check In');

  const [loading, setLoading] = useState(true);
  const [space, setSpace] = useState(null);
  const [siteLocation, setSiteLocation] = useState(null);
  const [activeCheckIn, setActiveCheckIn] = useState(null);
  const [notes, setNotes] = useState('');
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [duration, setDuration] = useState('');

  // Geofence configuration from site location
  const geofenceConfig = siteLocation?.geofence_enabled && siteLocation?.latitude && siteLocation?.longitude
    ? {
        latitude: parseFloat(siteLocation.latitude),
        longitude: parseFloat(siteLocation.longitude),
        radiusMeters: siteLocation.geofence_radius_meters || 100
      }
    : null;

  // Geolocation hook
  const {
    position,
    error: geoError,
    loading: geoLoading,
    permission,
    geofenceStatus,
    getPosition,
    supported: geoSupported
  } = useGeolocation({
    geofence: geofenceConfig
  });

  // Determine if geofence check is required
  const geofenceRequired = siteLocation?.geofence_enabled && geofenceConfig;
  const isStrictMode = siteLocation?.geofence_enforcement === 'strict';
  const isWarningMode = siteLocation?.geofence_enforcement === 'warning';
  const isOutsideGeofence = geofenceStatus && !geofenceStatus.isWithin;
  const isInsideGeofence = geofenceStatus && geofenceStatus.isWithin;

  // Determine if check-in is allowed based on enforcement mode
  // - No geofence required: always allowed
  // - Warning mode: always allowed (but flagged if outside)
  // - Strict mode: must have verified position AND be inside geofence
  const canCheckIn = !geofenceRequired ||
    isWarningMode ||
    (isStrictMode && position && isInsideGeofence);

  // Check if we're on HTTPS (required for geolocation on mobile)
  // localhost is considered secure even over HTTP
  const isSecureContext = window.isSecureContext ||
    window.location.protocol === 'https:' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: `/check-in/${qrCode}` } });
      return;
    }

    if (userProfile) {
      fetchSiteAndCheckIn();
    }
  }, [qrCode, user, userProfile]);

  // Request location when geofence is enabled and we don't have position yet
  useEffect(() => {
    // Don't auto-request if not on HTTPS (will fail on mobile)
    if (geofenceRequired && !position && !geoLoading && permission !== 'denied' && isSecureContext) {
      getPosition();
    }
  }, [geofenceRequired, position, geoLoading, permission, getPosition, isSecureContext]);

  // Duration timer - updates every second when checked in
  useEffect(() => {
    if (!activeCheckIn) {
      setDuration('');
      return;
    }

    const updateDuration = () => {
      const start = new Date(activeCheckIn.check_in_time);
      const now = new Date();
      const diff = now - start;

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      // Show seconds for first minute, then just hours and minutes
      if (hours === 0 && minutes === 0) {
        setDuration(`${seconds}s`);
      } else if (hours === 0) {
        setDuration(`${minutes}m ${seconds}s`);
      } else {
        setDuration(`${hours}h ${minutes}m`);
      }
    };

    // Update immediately
    updateDuration();

    // Then update every second
    const interval = setInterval(updateDuration, 1000);

    return () => clearInterval(interval);
  }, [activeCheckIn]);

  const fetchSiteAndCheckIn = async () => {
    try {
      setLoading(true);

      // Fetch space by QR code - include geofence fields
      const { data: siteLocationData, error: siteError } = await supabase
        .from('site_locations')
        .select(`
          *,
          projects(*)
        `)
        .eq('qr_code_data', qrCode)
        .eq('is_active', true)
        .single();

      if (siteError) throw siteError;

      if (!siteLocationData) {
        toast.error('Invalid QR code');
        return;
      }

      setSpace(siteLocationData.projects);
      setSiteLocation(siteLocationData);

      // Check if user already has an active check-in
      const { data: checkInData } = await supabase
        .from('site_checkins')
        .select('*')
        .eq('site_location_id', siteLocationData.id)
        .eq('user_id', userProfile.id)
        .is('check_out_time', null)
        .order('check_in_time', { ascending: false })
        .limit(1)
        .single();

      if (checkInData) {
        setActiveCheckIn(checkInData);
      }
    } catch (error) {
      console.error('Error fetching space:', error);
      if (error.code !== 'PGRST116') {
        toast.error('Failed to load space information');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!space || !siteLocation || !userProfile) return;

    // Validate geofence if strict mode
    if (geofenceRequired && isStrictMode && isOutsideGeofence) {
      toast.error('You must be within the geofence area to check in');
      return;
    }

    setCheckingIn(true);
    try {
      const checkInData = {
        site_location_id: siteLocation.id,
        project_id: space.id,
        user_id: userProfile.id,
        check_in_notes: notes || null,
        status: 'checked_in',
        // Track how the check-in was initiated
        check_in_method: isNFCSource ? 'nfc' : 'manual',
        // Location data
        check_in_latitude: position?.latitude || null,
        check_in_longitude: position?.longitude || null,
        // Geofence tracking
        geofence_distance_at_checkin: geofenceStatus?.distance || null,
        geofence_violation_at_checkin: geofenceRequired && isOutsideGeofence,
        geofence_enforcement_mode: siteLocation.geofence_enforcement || 'off'
      };

      const { data, error } = await supabase
        .from('site_checkins')
        .insert([checkInData])
        .select()
        .single();

      if (error) throw error;

      // Log geofence violation event if warning mode and outside
      if (geofenceRequired && isWarningMode && isOutsideGeofence && position) {
        await supabase.from('geofence_events').insert({
          checkin_id: data.id,
          site_location_id: siteLocation.id,
          user_id: userProfile.id,
          event_type: 'violation_at_checkin',
          latitude: position.latitude,
          longitude: position.longitude,
          distance_from_center: geofenceStatus.distance,
          notification_sent: false
        });
      }

      setActiveCheckIn(data);
      setNotes('');

      if (isOutsideGeofence && isWarningMode) {
        toast.warning('Checked in successfully, but you are outside the geofence area');
      } else {
        toast.success('Checked in successfully!');
      }
    } catch (error) {
      console.error('Error checking in:', error);
      toast.error('Failed to check in');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleCheckOut = async () => {
    if (!activeCheckIn) return;

    setCheckingOut(true);
    try {
      const { error } = await supabase
        .from('site_checkins')
        .update({
          check_out_time: new Date().toISOString(),
          check_out_notes: notes || null,
          check_out_latitude: position?.latitude || null,
          check_out_longitude: position?.longitude || null,
          status: 'checked_out'
        })
        .eq('id', activeCheckIn.id);

      if (error) throw error;

      setActiveCheckIn(null);
      setNotes('');
      toast.success('Checked out successfully!');
    } catch (error) {
      console.error('Error checking out:', error);
      toast.error('Failed to check out');
    } finally {
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <LoadingSpinner />
      </div>
    );
  }

  if (!space) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid QR Code</h2>
            <p className="text-gray-600 mb-4">
              This QR code is not valid or the space may have been deactivated.
            </p>
            <Button onClick={() => navigate('/')}>Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-md mx-auto pt-8 space-y-4">
        {/* Space Info Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isNFCSource ? 'bg-purple-100' : 'bg-blue-100'
              }`}>
                {isNFCSource ? (
                  <Nfc className="h-6 w-6 text-purple-600" />
                ) : (
                  <MapPin className="h-6 w-6 text-blue-600" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <CardTitle>{space.name}</CardTitle>
                  {isNFCSource && (
                    <Badge className="bg-purple-100 text-purple-700 border-purple-300 text-xs">
                      NFC
                    </Badge>
                  )}
                </div>
                {space.location && (
                  <p className="text-sm text-gray-600 mt-1">{space.location}</p>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Geofence Status Card - Only show if geofencing is enabled */}
        {geofenceRequired && !activeCheckIn && (
          <Card className={isOutsideGeofence ? 'border-yellow-300 bg-yellow-50' : 'border-green-300 bg-green-50'}>
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isOutsideGeofence ? 'bg-yellow-100' : 'bg-green-100'
                }`}>
                  {geoLoading ? (
                    <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                  ) : isOutsideGeofence ? (
                    <ShieldAlert className="h-5 w-5 text-yellow-600" />
                  ) : (
                    <ShieldCheck className="h-5 w-5 text-green-600" />
                  )}
                </div>
                <div className="flex-1">
                  {geoLoading ? (
                    <>
                      <p className="font-medium text-gray-900">Getting your location...</p>
                      <p className="text-sm text-gray-600">Please wait while we verify your position</p>
                    </>
                  ) : permission === 'denied' || !isSecureContext ? (
                    <>
                      <p className="font-medium text-red-900">Location Permission Required</p>
                      {!isSecureContext ? (
                        <p className="text-sm text-red-700 mb-2">
                          Location requires a secure (HTTPS) connection. Please access this page via HTTPS.
                        </p>
                      ) : (
                        <>
                          <p className="text-sm text-red-700 mb-2">
                            Location access is blocked. To enable it:
                          </p>
                          <ul className="text-xs text-red-700 list-disc list-inside space-y-1 mb-2">
                            <li><strong>iPhone:</strong> Settings → Safari → Location → Allow</li>
                            <li><strong>Android:</strong> Tap lock icon in address bar → Permissions</li>
                          </ul>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={getPosition}
                            className="mt-1"
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Try Again
                          </Button>
                        </>
                      )}
                      {isStrictMode && (
                        <p className="text-xs text-red-600 mt-2 font-medium">
                          ⚠️ Check-in is blocked until location is verified (strict mode)
                        </p>
                      )}
                    </>
                  ) : geoError ? (
                    <>
                      <p className="font-medium text-red-900">Location Error</p>
                      <p className="text-sm text-red-700">{geoError}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={getPosition}
                        className="mt-2"
                      >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Retry
                      </Button>
                      {isStrictMode && (
                        <p className="text-xs text-red-600 mt-2 font-medium">
                          ⚠️ Check-in is blocked until location is verified (strict mode)
                        </p>
                      )}
                    </>
                  ) : !position ? (
                    <>
                      <p className="font-medium text-gray-900">Location Required</p>
                      <p className="text-sm text-gray-600">
                        This site requires location verification for check-in
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={getPosition}
                        className="mt-2"
                      >
                        <Navigation className="h-4 w-4 mr-1" />
                        Enable Location
                      </Button>
                    </>
                  ) : isOutsideGeofence ? (
                    <>
                      <p className="font-medium text-yellow-900">Outside Geofence Area</p>
                      <p className="text-sm text-yellow-700">
                        You are {formatDistance(geofenceStatus.distance)} from the site
                        {isStrictMode && ' - check-in is blocked'}
                      </p>
                      {isStrictMode && (
                        <p className="text-xs text-yellow-600 mt-1">
                          Move within {formatDistance(siteLocation.geofence_radius_meters)} of the site to check in
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-green-900">Location Verified</p>
                      <p className="text-sm text-green-700">
                        You are {formatDistance(geofenceStatus.distance)} from the site center
                      </p>
                    </>
                  )}

                  {/* GPS Accuracy indicator */}
                  {position?.accuracy && (
                    <div className="mt-2 flex items-center gap-2">
                      <Target className="h-3 w-3 text-gray-400" />
                      <span className="text-xs text-gray-500">
                        GPS accuracy: ~{Math.round(position.accuracy)}m
                      </span>
                      <Badge
                        variant="outline"
                        className={`text-xs ${getAccuracyInfo(position.accuracy).bg} ${getAccuracyInfo(position.accuracy).color}`}
                      >
                        {getAccuracyInfo(position.accuracy).label}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Location Map - Only show when geofencing is enabled, we have site location, valid API key, and secure context */}
        {geofenceRequired && !activeCheckIn && geofenceConfig && hasValidMapKey && isSecureContext && permission !== 'denied' && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <MapIcon className="h-4 w-4 text-blue-600" />
                <CardTitle className="text-sm font-medium">Location Map</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <MapErrorBoundary>
                <div className="h-48 rounded-b-lg overflow-hidden">
                  <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                    <Map
                      mapId="checkin-map"
                      defaultCenter={{
                        lat: geofenceConfig.latitude,
                        lng: geofenceConfig.longitude
                      }}
                      defaultZoom={16}
                      gestureHandling="cooperative"
                      disableDefaultUI={true}
                      zoomControl={true}
                      className="w-full h-full"
                    >
                      <CheckInMapContent
                        siteCenter={{
                          lat: geofenceConfig.latitude,
                          lng: geofenceConfig.longitude
                        }}
                        userPosition={position}
                        radius={geofenceConfig.radiusMeters}
                        isInside={!isOutsideGeofence}
                      />
                    </Map>
                  </APIProvider>
                </div>
              </MapErrorBoundary>
              {/* Map Legend */}
              <div className="px-4 py-2 bg-gray-50 border-t flex items-center justify-center gap-4 text-xs text-gray-600">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-blue-500 border border-white shadow-sm" />
                  <span>Site</span>
                </div>
                {position && (
                  <div className="flex items-center gap-1.5">
                    <div className={`w-3 h-3 rounded-full border border-white shadow-sm ${
                      isOutsideGeofence ? 'bg-red-500' : 'bg-green-500'
                    }`} />
                    <span>You</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <div className={`w-4 h-2 rounded-sm border ${
                    isOutsideGeofence
                      ? 'bg-red-100 border-red-300'
                      : 'bg-green-100 border-green-300'
                  }`} />
                  <span>Geofence</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Check-In/Out Card */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            {activeCheckIn ? (
              <>
                {/* Currently Checked In */}
                <div className="text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-10 w-10 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">You're Checked In</h3>
                  <p className="text-gray-600 mb-4">
                    Since {new Date(activeCheckIn.check_in_time).toLocaleTimeString()}
                  </p>

                  {/* Duration */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-center gap-2 text-2xl font-bold text-gray-900">
                      <Clock className="h-6 w-6 text-blue-600" />
                      {duration}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Time on site</p>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label htmlFor="checkout-notes">Check-Out Notes (Optional)</Label>
                  <Textarea
                    id="checkout-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any notes before checking out..."
                    rows={3}
                  />
                </div>

                {/* Check Out Button */}
                <Button
                  onClick={handleCheckOut}
                  disabled={checkingOut}
                  className="w-full bg-red-600 hover:bg-red-700"
                  size="lg"
                >
                  {checkingOut ? (
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  ) : (
                    <LogOut className="h-5 w-5 mr-2" />
                  )}
                  Check Out
                </Button>
              </>
            ) : (
              <>
                {/* Ready to Check In */}
                <div className="text-center">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
                    geofenceRequired && isStrictMode && isOutsideGeofence
                      ? 'bg-gray-100'
                      : isNFCSource
                      ? 'bg-purple-100'
                      : 'bg-blue-100'
                  }`}>
                    {isNFCSource ? (
                      <Nfc className={`h-10 w-10 ${
                        geofenceRequired && isStrictMode && isOutsideGeofence
                          ? 'text-gray-400'
                          : 'text-purple-600'
                      }`} />
                    ) : (
                      <LogIn className={`h-10 w-10 ${
                        geofenceRequired && isStrictMode && isOutsideGeofence
                          ? 'text-gray-400'
                          : 'text-blue-600'
                      }`} />
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {isNFCSource ? 'Confirm Check-In' : 'Ready to Check In?'}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {geofenceRequired && isStrictMode && isOutsideGeofence
                      ? 'Move closer to the site to check in'
                      : isNFCSource
                      ? `Tap to confirm your arrival at ${space.name}`
                      : `Tap the button below to record your arrival at ${space.name}`
                    }
                  </p>
                </div>

                {/* Warning for outside geofence in warning mode */}
                {geofenceRequired && isWarningMode && isOutsideGeofence && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium">Outside Geofence Area</p>
                      <p className="text-yellow-700">
                        You can still check in, but this will be flagged as an out-of-area check-in.
                      </p>
                    </div>
                  </div>
                )}

                {/* Notes - Hide for NFC to streamline experience */}
                {!isNFCSource && (
                  <div>
                    <Label htmlFor="checkin-notes">Check-In Notes (Optional)</Label>
                    <Textarea
                      id="checkin-notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add any notes for this check-in..."
                      rows={3}
                    />
                  </div>
                )}

                {/* Check In Button - Larger for NFC */}
                <Button
                  onClick={handleCheckIn}
                  disabled={checkingIn || !canCheckIn || (geofenceRequired && geoLoading)}
                  className={`w-full ${
                    canCheckIn
                      ? isNFCSource
                        ? 'bg-purple-600 hover:bg-purple-700'
                        : 'bg-green-600 hover:bg-green-700'
                      : 'bg-gray-400'
                  }`}
                  size="lg"
                >
                  {checkingIn ? (
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  ) : isNFCSource ? (
                    <CheckCircle className="h-5 w-5 mr-2" />
                  ) : (
                    <LogIn className="h-5 w-5 mr-2" />
                  )}
                  {geofenceRequired && geoLoading
                    ? 'Verifying Location...'
                    : canCheckIn
                    ? isNFCSource
                      ? 'Confirm Check-In'
                      : 'Check In'
                    : isStrictMode && !position
                    ? 'Location Required'
                    : 'Move Closer to Check In'
                  }
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* User Info */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                {userProfile?.first_name?.[0]}
                {userProfile?.last_name?.[0]}
              </div>
              <div>
                <p className="font-medium text-gray-900">
                  {userProfile?.display_name ||
                    `${userProfile?.first_name} ${userProfile?.last_name}`}
                </p>
                <p className="text-sm text-gray-600">{userProfile?.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Help Text */}
        <div className="text-center text-sm text-gray-600">
          <p>Having trouble? Contact your space manager for assistance.</p>
        </div>
      </div>
    </div>
  );
};
